// =============================================================================
// TrackerSettings.jsx
//
// Dialog de gestão de lookups do Tracker.
// Acessível apenas a Admin e Manager.
//
// Lookups: Technology · Vendor · Type (Feature) · Team · Activity Owner
// Cada lookup tem uma tabela com Add / Edit inline / Delete
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Box, Typography, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, Switch, FormControlLabel, MenuItem, CircularProgress,
  Tooltip, Alert, Chip, Autocomplete,
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import AddIcon          from '@mui/icons-material/Add';
import EditIcon         from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon         from '@mui/icons-material/Save';
import CancelIcon       from '@mui/icons-material/Cancel';
import AxiosRequest from '../../../utils/AxiosRequest.js';
import config      from '../../../configuration/configuration.js';
import ImportTemplatesPanel from './ImportTemplatesPanel.jsx';
import ImportAccessPanel    from './ImportAccessPanel.jsx';

const BASE = `${config.API_URL}ActivityTrackerSettings`;

// ── API helpers ───────────────────────────────────────────────────────────────
const api = {
  get:    (path)       => AxiosRequest({ method: 'GET',    url: `${BASE}/${path}` }),
  save:   (path, data) => AxiosRequest({ method: 'POST',   url: `${BASE}/${path}`, data }),
  delete: (path, id)   => AxiosRequest({ method: 'DELETE', url: `${BASE}/${path}/${id}` }),
};

// Reaproveita o endpoint já existente para o "user picker" (mesmo usado em
// RadioDesignUserPrivileges > PrivilegeDialog.jsx) — devolve todos os
// AspNetUsers { id, userName, email }. Usado para o Activity Owner poder
// escolher a pessoa em vez de escrever Nome/User ID à mão (evita erros de
// escrita), tal como já foi feito no Import Access.
const apiGetAspNetUsers = () =>
  AxiosRequest({ method: 'GET', url: `${config.API_URL}RadioDesignUserPrivileges/users` });

// ── Lookup tabs config ────────────────────────────────────────────────────────
const TABS = [
  { key: 'Technologies', label: 'Technology',      hasTeam: false },
  { key: 'Vendors',      label: 'Vendor',          hasTeam: false },
  { key: 'Features',     label: 'Type',            hasTeam: false, hasColor: true },
  { key: 'Teams',        label: 'Team',            hasTeam: false },
  { key: 'Owners',       label: 'Activity Owner',  hasTeam: true, hasUserId: true },
  // Bulk Import (own components — not the generic LookupTable, see below)
  { key: 'ImportTemplates', label: 'Import Templates', custom: true },
  { key: 'ImportAccess',    label: 'Import Access',    custom: true },
];

// Cor por defeito para um Type novo (mesma usada como fallback no Gantt/Calendar)
const DEFAULT_FEATURE_COLOR = '#9BA3AF';

// ── Empty row factories ───────────────────────────────────────────────────────
const emptyRow = (hasColor) => ({
  id: null, name: '', isActive: true, sortOrder: 0,
  ...(hasColor ? { color: DEFAULT_FEATURE_COLOR } : {}),
});
const emptyOwner = () => ({ id: null, name: '', teamId: '', isActive: true, sortOrder: 0, userId: '' });

// =============================================================================
// UserPicker — substitui os campos manuais de Name + User ID por uma única
// pesquisa sobre os AspNetUsers reais (Name + User ID passam a ser copiados
// da conta escolhida, sem risco de erros de escrita).
// =============================================================================
// UserName no AspNetUsers é muitas vezes igual ao email de login — não serve
// como "nome apresentável" sozinho. Name (FirstName+LastName) é a escolha
// certa quando existe; só cai para UserName/Email se a conta não tiver
// FirstName/LastName preenchidos.
const displayName = (u) => (u.name?.trim()) || u.userName || u.email || '';

const UserPicker = ({ aspNetUsers, loading, userId, onChange }) => {
  const selected = aspNetUsers.find(
    (u) => u.email && userId && u.email.toLowerCase() === userId.toLowerCase()
  ) ?? null;

  return (
    <Autocomplete
      size="small"
      options={aspNetUsers}
      loading={loading}
      value={selected}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(u) => displayName(u)}
      onChange={(_, u) => onChange({ name: u ? displayName(u) : '', userId: u?.email ?? '' })}
      sx={{ minWidth: 320 }}
      renderOption={(props, u) => (
        <li {...props} key={u.id}>
          <Box>
            <Typography variant="body2">{displayName(u)}</Typography>
            <Typography variant="caption" color="text.secondary">{u.email}</Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Person"
          placeholder="Search name or email…"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

// =============================================================================
// LookupTable — tabela genérica para um lookup
// =============================================================================
const LookupTable = ({ tabKey, hasTeam, hasColor, hasUserId, teams, aspNetUsers, loadingUsers }) => {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [editId,     setEditId]     = useState(null);
  const [editForm,   setEditForm]   = useState(null);
  const [teamFilter, setTeamFilter] = useState('');  // '' = all teams

  // Rows filtered by selected team (only for Owners tab)
  const filteredRows = hasTeam && teamFilter
    ? rows.filter((r) => String(r.teamId) === teamFilter)
    : rows;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(tabKey)
      .then((res) => setRows(res.data ?? []))
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [tabKey]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditId(row.id ?? 'new');
    setEditForm({ ...row, teamId: row.teamId ? String(row.teamId) : '' });
  };

  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  const handleSave = () => {
    if (!editForm.name?.trim()) return;
    setSaving(true);
    setError(null);

    const payload = hasTeam
      ? { ...editForm, id: editId === 'new' ? null : editId, teamId: Number(editForm.teamId) }
      : { ...editForm, id: editId === 'new' ? null : editId };

    const endpoint = tabKey;
    api.save(endpoint, payload)
      .then(() => { cancelEdit(); load(); })
      .catch(() => setError('Failed to save.'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Deactivate this item? It will no longer appear in dropdowns.')) return;
    setSaving(true);
    api.delete(tabKey, id)
      .then(() => load())
      .catch(() => setError('Failed to delete.'))
      .finally(() => setSaving(false));
  };

  const handleAddNew = () => {
    startEdit(hasTeam ? emptyOwner() : emptyRow(hasColor));
  };

  const colCount = 4 + (hasTeam ? 1 : 0) + (hasColor ? 1 : 0) + (hasUserId ? 1 : 0);
  const cellSx = { fontSize: '0.78rem', py: 0.75 };
  const headSx = { fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase',
                   letterSpacing: 0.8, color: 'rgba(255,255,255,0.85)', bgcolor: 'primary.main',
                   py: 1, borderBottom: 'none' };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {hasUserId && (
        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.78rem' }}>
          Pick the <strong>Person</strong> from the list — Name and User ID are copied straight from
          their account, so there's no risk of typos breaking their permission to edit or delete
          the activities where they're set as Owner.
        </Alert>
      )}

      {/* ── Team filter — só no tab Owners ── */}
      {hasTeam && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select size="small" label="Filter by Team"
            value={teamFilter}
            onChange={(e) => { setTeamFilter(e.target.value); cancelEdit(); }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value=""><em>All Teams</em></MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
            ))}
          </TextField>
          {teamFilter && (
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} owner{filteredRows.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {hasTeam && <TableCell sx={{ ...headSx, bgcolor: 'primary.main' }}>Team</TableCell>}
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main' }}>Name</TableCell>
              {hasColor && <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 70 }}>Color</TableCell>}
              {hasUserId && <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 160 }}>User ID</TableCell>}
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 70 }}>Order</TableCell>
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 80 }}>Active</TableCell>
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {teamFilter ? 'No owners for this team.' : 'No items yet.'}
                </TableCell>
              </TableRow>
            ) : filteredRows.map((row) => {
              const isEditing = editId === row.id;
              return (
                <TableRow key={row.id} sx={{ opacity: row.isActive ? 1 : 0.45 }}>
                  {hasTeam && (
                    <TableCell sx={cellSx}>
                      {isEditing ? (
                        <TextField select size="small" value={editForm.teamId}
                          onChange={(e) => setEditForm((p) => ({ ...p, teamId: e.target.value }))}
                          sx={{ minWidth: 140 }}>
                          {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                        </TextField>
                      ) : (
                        teams.find((t) => t.id === row.teamId)?.name ?? '—'
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={cellSx} colSpan={isEditing && hasUserId ? 2 : undefined}>
                    {isEditing ? (
                      hasUserId ? (
                        <UserPicker
                          aspNetUsers={aspNetUsers}
                          loading={loadingUsers}
                          userId={editForm.userId}
                          onChange={({ name, userId }) => setEditForm((p) => ({ ...p, name, userId }))}
                        />
                      ) : (
                        <TextField size="small" value={editForm.name} autoFocus
                          onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelEdit(); }}
                          sx={{ minWidth: 200 }} />
                      )
                    ) : (
                      <Typography variant="body2" fontWeight={row.isActive ? 500 : 400}>
                        {row.name}
                      </Typography>
                    )}
                  </TableCell>
                  {hasColor && (
                    <TableCell sx={cellSx}>
                      {isEditing ? (
                        <Box
                          component="input"
                          type="color"
                          value={editForm.color || DEFAULT_FEATURE_COLOR}
                          onChange={(e) => setEditForm((p) => ({ ...p, color: e.target.value }))}
                          sx={{
                            width: 36, height: 28, p: 0, border: '1px solid', borderColor: 'divider',
                            borderRadius: 1, cursor: 'pointer', bgcolor: 'transparent',
                          }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{
                            width: 16, height: 16, borderRadius: '50%',
                            bgcolor: row.color || DEFAULT_FEATURE_COLOR,
                            border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0,
                          }} />
                          <Typography variant="caption" color="text.secondary">
                            {row.color || '—'}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  )}
                  {hasUserId && !isEditing && (
                    <TableCell sx={cellSx}>
                      <Typography variant="caption" color={row.userId ? 'text.secondary' : 'text.disabled'}>
                        {row.userId || 'Not linked'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <TextField size="small" type="number" value={editForm.sortOrder}
                        onChange={(e) => setEditForm((p) => ({ ...p, sortOrder: Number(e.target.value) }))}
                        sx={{ width: 60 }} />
                    ) : row.sortOrder}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <Switch size="small" checked={editForm.isActive}
                        onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                    ) : (
                      <Chip label={row.isActive ? 'Active' : 'Inactive'} size="small"
                        color={row.isActive ? 'success' : 'default'}
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => startEdit(row)} disabled={saving || editId !== null}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} disabled={saving || editId !== null}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* New row inline */}
            {editId === 'new' && (
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {hasTeam && (
                  <TableCell sx={cellSx}>
                    <TextField select size="small" value={editForm.teamId}
                      onChange={(e) => setEditForm((p) => ({ ...p, teamId: e.target.value }))}
                      sx={{ minWidth: 140 }}>
                      {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                    </TextField>
                  </TableCell>
                )}
                <TableCell sx={cellSx} colSpan={hasUserId ? 2 : undefined}>
                  {hasUserId ? (
                    <UserPicker
                      aspNetUsers={aspNetUsers}
                      loading={loadingUsers}
                      userId={editForm.userId}
                      onChange={({ name, userId }) => setEditForm((p) => ({ ...p, name, userId }))}
                    />
                  ) : (
                    <TextField size="small" value={editForm.name} autoFocus
                      placeholder="Name…"
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelEdit(); }}
                      sx={{ minWidth: 200 }} />
                  )}
                </TableCell>
                {hasColor && (
                  <TableCell sx={cellSx}>
                    <Box
                      component="input"
                      type="color"
                      value={editForm.color || DEFAULT_FEATURE_COLOR}
                      onChange={(e) => setEditForm((p) => ({ ...p, color: e.target.value }))}
                      sx={{
                        width: 36, height: 28, p: 0, border: '1px solid', borderColor: 'divider',
                        borderRadius: 1, cursor: 'pointer', bgcolor: 'transparent',
                      }}
                    />
                  </TableCell>
                )}
                <TableCell sx={cellSx}>
                  <TextField size="small" type="number" value={editForm.sortOrder}
                    onChange={(e) => setEditForm((p) => ({ ...p, sortOrder: Number(e.target.value) }))}
                    sx={{ width: 60 }} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Switch size="small" checked={editForm.isActive}
                    onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Save">
                      <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={handleAddNew} disabled={editId !== null || loading}>
          Add {TABS.find((t) => t.key === tabKey)?.label}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// TrackerSettings — Dialog principal
// =============================================================================
const TrackerSettings = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('Technologies');
  const [teams,     setTeams]     = useState([]);
  const [aspNetUsers,   setAspNetUsers]   = useState([]);
  const [loadingUsers,  setLoadingUsers]  = useState(false);

  // Load teams once — needed for Owner dropdown
  // Uses the public tracker endpoint (not the settings one) to avoid auth issues
  useEffect(() => {
    if (!open) return;
    AxiosRequest({ method: 'GET', url: `${config.API_URL}ActivityTracker/GetTeams` })
      .then((res) => setTeams(res.data ?? []))
      .catch(() => {});
  }, [open]);

  // Load AspNetUsers once — needed for the Activity Owner "Person" picker
  // (replaces manually typing Name + User ID, same idea as Import Access).
  useEffect(() => {
    if (!open) return;
    setLoadingUsers(true);
    apiGetAspNetUsers()
      .then((res) => setAspNetUsers(res.data ?? []))
      .catch(() => setAspNetUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [open]);

  const currentTab = TABS.find((t) => t.key === activeTab);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700} fontSize="1.05rem">Tracker Settings</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Tab selector ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          {TABS.map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                px: 2, py: 0.6, borderRadius: 2, cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600,
                bgcolor: activeTab === tab.key ? 'primary.main' : 'action.hover',
                color:   activeTab === tab.key ? 'white' : 'text.primary',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: activeTab === tab.key ? 'primary.dark' : 'action.selected' },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        {/* ── Active lookup table (or the Bulk Import custom panels) ── */}
        {activeTab === 'ImportTemplates' ? (
          <ImportTemplatesPanel key={activeTab} />
        ) : activeTab === 'ImportAccess' ? (
          <ImportAccessPanel key={activeTab} />
        ) : (
          <LookupTable
            key={activeTab}
            tabKey={activeTab}
            hasTeam={currentTab?.hasTeam ?? false}
            hasColor={currentTab?.hasColor ?? false}
            hasUserId={currentTab?.hasUserId ?? false}
            teams={teams}
            aspNetUsers={aspNetUsers}
            loadingUsers={loadingUsers}
          />
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrackerSettings;